// Chat API — wraps the Messaging endpoints (ported from messaging.js).
// /history returns { messages: [{content, content_type, media_url, timestamp(sec), metadata:{role}}] }
// /message returns an array of MessageResponse; 402 → paywall (ApiError.status).
// /start returns a single MessageResponse.

import { api } from './client'
import type {
  EngineScaleResponse,
  EngineSkillsResponse,
  MessageMetadata,
  ServerMessage,
} from '../types/api'

// Local (client-side) message shape held in messagingStore.
export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  contentType: string
  mediaUrl: string | null
  timestamp: number
  reactions: string[]
  metadata?: MessageMetadata | null
}

// A raw record as returned by /history or a WS/HTTP message frame.
type RawServerRecord = ServerMessage & { timestamp?: number }

// Normalize a server history/message record into the local message shape.
export function normalizeServerMessage(msg: RawServerRecord, fallbackRole: 'user' | 'assistant' = 'assistant'): ChatMessage {
  return {
    role: msg.metadata?.role || fallbackRole,
    content: msg.content,
    contentType: msg.content_type || 'text',
    mediaUrl: msg.media_url || null,
    // Server stores seconds; local timestamps are ms.
    timestamp: (msg.timestamp || 0) * 1000,
    reactions: [],
    metadata: msg.metadata || null,
  }
}

interface HistoryResponse {
  messages: RawServerRecord[]
}

export const chatApi = {
  start: () => api.post<ServerMessage>('/start'),
  history: (limit = 50) => api.get<HistoryResponse>(`/history?limit=${limit}`),
  send: (content: string) => api.post<ServerMessage[]>('/message', { message: content, content_type: 'text' }),
  clearHistory: () => api.del('/history'),
  // Engine v2 (409 when the user is not engine-enabled — callers fall back)
  engineSkills: () => api.get<EngineSkillsResponse>('/engine/skills'),
  engineLaunch: (skillId: string) => api.post<ServerMessage>('/engine/launch', { skill_id: skillId }),
  engineScale: (payload: { kind: 'intensity' | 'usefulness' | 'skip'; point?: string; value?: number }) =>
    api.post<EngineScaleResponse>('/engine/scale', payload),
  // §0.6 handover after payment success (idempotent; the message path
  // retriggers it lazily when this call is lost)
  engineHandover: (planId?: string) =>
    api.post<{ status: string; seed?: string | null; replies?: string[] }>('/engine/handover', planId ? { plan_id: planId } : {}),
}

export interface EngineStyleState {
  style: string
  enabled: boolean
  labels: Record<string, { label: string; hint: string }>
}

export const engineStyleApi = {
  get: () => api.get<EngineStyleState>('/engine/style'),
  set: (style: string) => api.put<EngineStyleState>('/engine/style', { style }),
}
